import { lazy, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import PrivatePublicRoute from "./Components/common/PrivatePublicRoute";
import PrivateAuthRoutes from "./Components/common/PrivateAuthRoutes";
import ProtectedRoutes from "./Components/common/ProtectedRoutes";
import PublicRoutesSuspense from "./Components/Layout/PublicRoutesSuspense";
import PrivateRoutesSuspense from "./Components/Layout/PrivateRoutesSuspense";
import NotFound from "./pages/NotFound";
import { useDispatch, useSelector } from "react-redux";
import { clearTimes } from "./Redux/Slice/authSlice";
import ShowTimer from "./Components/common/ShowTimer";
import { userLogTimeUpdateAction } from "./Redux/Action/authAction";
import {
  clearPlatformVisitMarkers,
  recordPlatformVisit,
} from "./Util/visitTracking";
import { hasMemberOrganisationAccess, isOrganisationGroupUser } from "./OrganisationGroups/utils/roleAccess";
import Confirmation from "./pages/Confirmation";
import ResetPage from "./pages/ResetPage";
import CollectionShorts from "./pages/CollectionShorts";
import ManageAdmin from "./pages/ManageAdmin";
import AdminDetail from "./pages/AdminDetail";
import SharedCollection from "./pages/SharedCollection";
import RequestedPacks from "./pages/Packs/RequestedPacks";
import OrganisationRoutes from "./OrganisationGroups/routes/OrganisationRoutes";


const Login = lazy(() => import("./pages/Login"));
const TryPeerscroller = lazy(() => import("./pages/trial account/TryPeerscroller"));
const RegistrationSuccess = lazy(() => import("./pages/trial account/RegistrationSuccess"));
const VerifyEmail = lazy(() => import("./pages/trial account/VerifyEmail"));
const MicrosoftCallback = lazy(() => import("./pages/MicrosoftCallback"));
const GoogleCallback = lazy(() => import("./pages/GoogleCallback"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const FAQs = lazy(() => import("./pages/FAQs"));
const Otp = lazy(() => import("./pages/Otp"));
const Browse = lazy(() => import("./pages/Browse"));
const Playback = lazy(() => import("./pages/Playback"));
const PlaybackDemo = lazy(() => import("./pages/PlaybackDemo"));
const Classes = lazy(() => import("./pages/Classes"));
const ClassDetail = lazy(() => import("./pages/ClassDetail"));
const Students = lazy(() => import("./pages/Students"));
const Parents = lazy(() => import("./pages/Parents"));
const StudentDetail = lazy(() => import("./pages/StudentDetail"));
const ParentDetail = lazy(() => import("./pages/ParentDetail"));
const Collections = lazy(() => import("./pages/Collections"));
const CollectionDetail = lazy(() => import("./pages/CollectionDetail"));
const Likes = lazy(() => import("./pages/Likes"));
const Settings = lazy(() => import("./pages/Settings"));
const Packs = lazy(() => import("./pages/Packs/Packs"));
const PackDetails = lazy(() => import("./pages/Packs/PackDetails"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const HelpAndSupport = lazy(() => import("./pages/HelpAndSupport"));
const ReportAnIssue = lazy(() => import("./pages/ReportAnIssue"));
const Teachers = lazy(() => import("./pages/Teachers"));
const CollegePolicy = lazy(() => import("./pages/CollegePolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const SellerPolicy = lazy(() => import("./pages/SellerPolicy"));
const SponsorPolicy = lazy(() => import("./pages/SponsorPolicy"));
const AcceptableUsePolicy = lazy(() => import("./pages/AcceptableUsePolicy"));
const VideoShorts = lazy(() => import("./pages/VideoShorts"));
const TeacherDetail = lazy(() => import("./pages/TeacherDetail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SchoolDetails = lazy(() => import("./pages/SchoolDetails"));
const TeacherDetails = lazy(() => import("./pages/TeacherDetails"));
const TeacherPortalDetail = lazy(() => import("./pages/TeacherPortalDetail"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const StudentSetPassword = lazy(() => import("./pages/StudentSetPassword"));
const ParentPasswordSet = lazy(() => import("./pages/ParentPasswordSet"));
const MyLikes = lazy(() => import("./pages/MyLikes"));
const ResourceMapping = lazy(() => import("./pages/ResourceMapping"));
const ReviewAgreement = lazy(() => import("./pages/ReviewAgreement"));

function App() {
  const dispatch = useDispatch();
  const userDate = useSelector((state) => state?.authSlice.userData?.payload);
  const hours = useSelector((state) => state?.authSlice.hours);
  const tokenFromStore = useSelector(
    (state) => state?.authSlice?.userData?.payload?.token
  );
  const rehydrated = useSelector((state) => state?.authSlice?._persist?.rehydrated);
  const tokenData = tokenFromStore || null;
  const isOnlyOrganisationUser = isOrganisationGroupUser(userDate) && !hasMemberOrganisationAccess(userDate);

  const activeStatus = async (currentDayName) => {
    if (isOnlyOrganisationUser) return;

    try {
      const Payload = {
        date: new Date(),
        hours: hours === null ? 0 : hours,
        id: userDate?.activityId,
        day: currentDayName,
      };
      const token = localStorage.getItem("token");
      if (token) {
        const response = await dispatch(userLogTimeUpdateAction(Payload));
        if (response?.payload?.statusCode === 200) {
          dispatch(clearTimes());
        }
      } else {
        console.log("no token");
      }
    } catch (error) {
      console.error("Error fetching initial time:", error);
    }
  };

  useEffect(() => {
    if (isOnlyOrganisationUser) return;


    const checkTimeAndCallApi = () => {
      const currentTime = new Date();
      const currentHour = currentTime.getHours();
      const currentMinute = currentTime.getMinutes();
      const daysOfWeek = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const currentDayName = daysOfWeek[currentTime.getDay()];


      if (currentHour === 23 && currentMinute === 59) {

        const token = localStorage.getItem("token");
        if (token) {
          activeStatus(currentDayName);
        } else {
          console.log("User is not logged in.");
        }
      }
    };


    const intervalId = setInterval(checkTimeAndCallApi, 60000); // 60000ms = 1 minute


    return () => clearInterval(intervalId);
  }, [isOnlyOrganisationUser]);
  useEffect(() => {
    if (!rehydrated || !tokenData) return;
    recordPlatformVisit(dispatch, tokenData, userDate);

  }, [rehydrated, tokenData]);

  useEffect(() => {

    if (tokenData) return;
    clearPlatformVisitMarkers();
  }, [tokenData]);

  return (
    <div className="App">
      <>
        {tokenData ? (
          !isOnlyOrganisationUser ? (
            <ShowTimer
              hours={hours === null ? 0 : hours}
              userDate={userDate}
            />
          ) : null
        ) : null}
      </>

      <BrowserRouter>
        <Routes>
          <Route element={<PrivatePublicRoute />}>
            <Route
              path="/review-agreement"
              element={
                <PublicRoutesSuspense>
                  <ReviewAgreement />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <PublicRoutesSuspense>
                  <PrivacyPolicy />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/college-policy"
              element={
                <PublicRoutesSuspense>
                  <CollegePolicy />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/acceptable-use-policy"
              element={
                <PublicRoutesSuspense>
                  <AcceptableUsePolicy />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/sponsor-policy"
              element={
                <PublicRoutesSuspense>
                  <SponsorPolicy />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/seller-policy"
              element={
                <PublicRoutesSuspense>
                  <SellerPolicy />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/confirmation"
              element={
                <PublicRoutesSuspense>
                  <Confirmation />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/"
              element={
                <PublicRoutesSuspense>
                  <Login />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoutesSuspense>
                  <TryPeerscroller />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/registration-success"
              element={
                <PublicRoutesSuspense>
                  <RegistrationSuccess />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/trial/verify-email"
              element={
                <PublicRoutesSuspense>
                  <VerifyEmail />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/microsoft"
              element={
                <PublicRoutesSuspense>
                  <MicrosoftCallback />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/google"
              element={
                <PublicRoutesSuspense>
                  <GoogleCallback />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/otp"
              element={
                <PublicRoutesSuspense>
                  <Otp />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoutesSuspense>
                  <ResetPassword />
                </PublicRoutesSuspense>
              }
            />

            <Route
              path="/reset-page/:id"
              element={
                <PublicRoutesSuspense>
                  <ResetPage />
                </PublicRoutesSuspense>
              }
            />

            <Route
              path="/forgot-password"
              element={
                <PublicRoutesSuspense>
                  <ForgotPassword />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/set-password/:id"
              element={
                <PublicRoutesSuspense>
                  <SetPassword />
                </PublicRoutesSuspense>
              }
            />
            <Route
              path="/student/set-password/:id"
              element={
                <PublicRoutesSuspense>
                  <StudentSetPassword />
                </PublicRoutesSuspense>
              }
            />

            <Route
              path="/user/set-password/:id"
              element={
                <PublicRoutesSuspense>
                  <ParentPasswordSet />
                </PublicRoutesSuspense>
              }
            />
          </Route>

          <Route element={<PrivateAuthRoutes />}>
            <Route element={<PrivateRoutesSuspense />}>
              <Route path="/video-shorts/:id/:videoIds" element={<VideoShorts />} />
              <Route
                path="/collection-shorts/:id/:videoIds"
                element={<CollectionShorts />}
              />
              <Route path="/students" element={<Students />} />
              <Route path="/parents" element={<Parents />} />
              <Route path="/manage-admin" element={<ManageAdmin />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/faqs" element={<FAQs />} />
              <Route path="/browse" element={<Browse />} />
              <Route path="/playback-demo/:id" element={<PlaybackDemo />} />
              <Route path="/playback/:id" element={<Playback />} />
              <Route path="/my-likes" element={<MyLikes />} />
              <Route path="/classes" element={<Classes />} />
              <Route path="/class-detail/:id" element={<ClassDetail />} />
              <Route path="/student-detail/:id" element={<StudentDetail />} />
              <Route path="/parent-detail/:id" element={<ParentDetail />} />
              <Route path="/collections" element={<Collections />} />
              <Route path="/collection-detail/:id" element={<CollectionDetail />} />
              <Route path="/shared-collection/:type/:id" element={<SharedCollection />} />
              <Route path="/likes" element={<Likes />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/packs" element={<Packs />} />
              <Route path="/requested-packs" element={<RequestedPacks />} />
              <Route path="/pack-details/:id" element={<PackDetails />} />
              <Route path="/update-password" element={<UpdatePassword />} />
              <Route path="/help-support" element={<HelpAndSupport />} />
              <Route path="/resource-mapping" element={<ResourceMapping />} />
              <Route path="/report-issue" element={<ReportAnIssue />} />
              <Route path="/teacher-profile" element={<TeacherDetails />} />

              <Route element={<ProtectedRoutes />}>
                <Route path="/teachers" element={<Teachers />} />
                <Route path="/school-profile" element={<SchoolDetails />} />
              </Route>

              <Route path="/teacher-detail/:id" element={<TeacherDetail />} />
              <Route path="/admin-detail/:id" element={<AdminDetail />} />
              <Route path="/teacher-portal-detail" element={<TeacherPortalDetail />} />
            </Route>


            <Route path="/organisations/*" element={<OrganisationRoutes />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster />

      </BrowserRouter>
    </div>
  );
}

export default App;
